import { AgnesAlert, AgnesAlertContent, AgnesBadge, AgnesButton, AgnesModal } from "@agnes/ui";
import { Check, Info, ShieldCheck } from "lucide-react";
import { BaseComponentProps } from "@/components/BaseComponentProps";
import type { ResumoAprovacao } from "@/components/rubricas/processamentoLote";

export interface ModalAprovacaoLoteProps extends BaseComponentProps {
  open: boolean;
  resumo: ResumoAprovacao;
  aprovando: boolean;
  onClose: () => void;
  onConfirmar: () => void;
}

const MAX_NATUREZAS = 6;
const MAX_DESCRICOES = 8;

export function ModalAprovacaoLote({
  open,
  resumo,
  aprovando,
  onClose,
  onConfirmar,
  dataQa = "rubricas-aprovacao-lote",
}: ModalAprovacaoLoteProps) {
  const { descricoes } = resumo;
  const naturezas = [...resumo.naturezas].sort((a, b) => a - b);
  const total = resumo.total.toLocaleString("pt-BR");

  return (
    <AgnesModal
      dataQa={dataQa}
      open={open}
      maxWidth="md"
      title="Confirmar aprovação em lote"
      onClose={() => !aprovando && onClose()}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton
            dataQa={`${dataQa}-cancelar`}
            variant="text"
            disabled={aprovando}
            onClick={onClose}
          >
            Cancelar
          </AgnesButton>
          <AgnesButton
            dataQa={`${dataQa}-confirmar`}
            variant="primary"
            className="bg-agnes-success hover:bg-[#006144]"
            startIcon={<Check className="h-4 w-4" />}
            loading={aprovando}
            disabled={resumo.total === 0}
            onClick={onConfirmar}
          >
            {aprovando ? "Processando…" : "Confirmar aprovação"}
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-3 px-6 py-4 text-sm text-agnes-text-primary">
        <p data-qa={`${dataQa}-resumo`} className="text-agnes-text-muted">
          {total} {resumo.total === 1 ? "rubrica será movida" : "rubricas serão movidas"} para
          “Analisado”.
        </p>

        <AgnesAlert
          dataQa={`${dataQa}-escopo`}
          icon={<Info className="h-4 w-4 text-agnes-primary" />}
          className="bg-agnes-primary/5"
        >
          <AgnesAlertContent dataQa={`${dataQa}-escopo`}>
            Você está aprovando <b>{total}</b> {resumo.total === 1 ? "rubrica" : "rubricas"} em{" "}
            <b>{descricoes.length}</b> {descricoes.length === 1 ? "descrição" : "descrições"}
            {naturezas.length > 0 && (
              <>
                , abrangendo as naturezas{" "}
                <b>
                  {naturezas.slice(0, MAX_NATUREZAS).join(", ")}
                  {naturezas.length > MAX_NATUREZAS ? "…" : ""}
                </b>
              </>
            )}
            . A descrição permanece intacta — apenas o status é atualizado.
          </AgnesAlertContent>
        </AgnesAlert>

        <AgnesAlert
          dataQa={`${dataQa}-risco`}
          icon={<ShieldCheck className="h-4 w-4 text-agnes-success" />}
          className="bg-agnes-success-light"
        >
          <AgnesAlertContent dataQa={`${dataQa}-risco`}>
            O <b>grau de risco segue o cálculo automático</b> da Agnes (exceções manuais são
            preservadas) e o valor atual de cada rubrica fica registrado no <b>histórico</b> para
            auditoria.
          </AgnesAlertContent>
        </AgnesAlert>

        <div data-qa={`${dataQa}-descricoes`} className="flex flex-wrap gap-1.5">
          {descricoes.slice(0, MAX_DESCRICOES).map((descricao) => (
            <AgnesBadge key={descricao} variant="secondary" size="sm">
              {descricao}
            </AgnesBadge>
          ))}
          {descricoes.length > MAX_DESCRICOES && (
            <AgnesBadge variant="secondary" size="sm">
              +{descricoes.length - MAX_DESCRICOES}
            </AgnesBadge>
          )}
        </div>
      </div>
    </AgnesModal>
  );
}
